/**
 * WeatherSystem — client-side weather rendering, audio cues, and gameplay hooks (DON-601).
 *
 * Consumes OverworldRoomState.weather (server-authoritative) and drives:
 *   - Rain: GPU particle emitter (droplets falling at angle)
 *   - Storm: periodic lightning flash overlay + thunder audio hook
 *   - Horizon darkening announcement (§14.4) 5 s before arrival
 *   - IWindProvider for WindSystem (wind intensity/direction per weather state)
 *   - Gameplay hook events via scene.events:
 *       WeatherSystem.EVENTS.CHANGE          → WeatherChangePayload
 *       WeatherSystem.EVENTS.WET_GROUND      → { wet: boolean }
 *       WeatherSystem.EVENTS.WIND_ARROWS     → { intensity: number, direction: number }
 *       WeatherSystem.EVENTS.NPC_SHELTER     → { seek: boolean }
 *       WeatherSystem.EVENTS.PHANTOM_SHOP    → { active: boolean }
 *       WeatherSystem.EVENTS.PLANT_GROWTH    → { active: boolean }
 *       WeatherSystem.EVENTS.WINDMILL_POWER  → { powered: boolean }
 *
 * Implements IWindProvider so WindSystem can be driven by real weather wind data.
 *
 * All timing uses delta ms — never frame counts (§1.8).
 */

import * as Phaser from 'phaser';
import type { IWindProvider } from '../ui/WindSystem';
import { isWebGL } from '../rendering/rendererUtils';
import {
  type WeatherState,
  type WeatherChangePayload,
  WEATHER_WIND_INTENSITY,
  WEATHER_WIND_DIRECTION,
  WEATHER_HOOK,
} from '@game/shared';

// ── Constants ───────────────────────────────────────────────────────────────

const RAIN_TEXTURE_KEY       = '__weather_rain_drop__';
const _LIGHTNING_OVERLAY_KEY  = '__weather_lightning__';

/** Number of GPU rain-drop particles */
const RAIN_DROP_COUNT   = 120;
/** Rain drop fall speed at full intensity (px/s) */
const RAIN_FALL_SPEED   = 480;
/** Slight horizontal drift angle for rain (radians from vertical) */
const RAIN_ANGLE_RAD    = 0.22;

/** Storm lightning flash interval range (ms) */
const LIGHTNING_MIN_MS  = 4_000;
const LIGHTNING_MAX_MS  = 12_000;
/** Lightning flash duration (ms) */
const LIGHTNING_FLASH_MS = 180;

/** Depth for weather particle layers */
const WEATHER_DEPTH     = 90;
/** Depth for the lightning overlay (above everything except HUD) */
const LIGHTNING_DEPTH   = 200;
/** Depth for the horizon darkening overlay */
const HORIZON_DEPTH     = 88;

/** How quickly the horizon overlay fades in/out (ms) */
const HORIZON_FADE_MS   = 2_000;

// ── WeatherSystem ────────────────────────────────────────────────────────────

export class WeatherSystem implements IWindProvider {
  static readonly EVENTS = {
    CHANGE:         'weather:change',
    WET_GROUND:     WEATHER_HOOK.WET_GROUND,
    WIND_ARROWS:    WEATHER_HOOK.WIND_ARROWS,
    NPC_SHELTER:    WEATHER_HOOK.NPC_SHELTER,
    PHANTOM_SHOP:   WEATHER_HOOK.PHANTOM_SHOP,
    PLANT_GROWTH:   WEATHER_HOOK.PLANT_GROWTH,
    WINDMILL_POWER: WEATHER_HOOK.WINDMILL_POWER,
  } as const;

  private readonly scene: Phaser.Scene;

  private current: WeatherState = 'clear';

  // GPU rain layer
  private rainLayer: Phaser.GameObjects.SpriteGPULayer | null = null;

  // Lightning
  private lightningOverlay: Phaser.GameObjects.Rectangle | null = null;
  private lightningTimer:   Phaser.Time.TimerEvent | null = null;

  // Horizon darkening overlay
  private horizonOverlay: Phaser.GameObjects.Rectangle | null = null;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  // ── IWindProvider ────────────────────────────────────────────────────────

  getWindDirection(): number {
    return WEATHER_WIND_DIRECTION[this.current];
  }

  getWindIntensity(): number {
    return WEATHER_WIND_INTENSITY[this.current];
  }

  // ── Public API ───────────────────────────────────────────────────────────

  /** Current weather state. */
  getWeather(): WeatherState {
    return this.current;
  }

  /**
   * Apply a new weather state received from the server.
   * Call from OverworldScene whenever room.state.weather changes or
   * MSG_WEATHER_CHANGE is received.
   *
   * @param next         New weather state.
   * @param isAnnouncement  True = 5-s lead-time warning; false = actual arrival.
   */
  applyWeather(next: WeatherState, isAnnouncement: boolean = false): void {
    const previous = this.current;

    if (isAnnouncement) {
      // Darken horizon as a preview of incoming weather (§14.4)
      if (next !== 'clear') {
        this.showHorizonWarning(next);
      }
      this.scene.events.emit(WeatherSystem.EVENTS.CHANGE, {
        previous,
        current: next,
        isAnnouncement: true,
      } satisfies WeatherChangePayload);
      return; // don't change visual state yet
    }

    if (next === previous) return;

    this.current = next;

    // Tear down previous effects
    this.stopRain();
    this.stopLightning();
    this.hideHorizonWarning();

    // Start new effects
    if (next === 'rain')  this.startRain();
    if (next === 'storm') { this.startRain(); this.startLightning(); }

    // Emit gameplay hooks
    this.emitGameplayHooks(previous, next);

    this.scene.events.emit(WeatherSystem.EVENTS.CHANGE, {
      previous,
      current: next,
      isAnnouncement: false,
    } satisfies WeatherChangePayload);
  }

  /**
   * Per-frame update. Scenes should call once from update(time, delta).
   * Currently lightweight — GPU animations are self-driving.
   */
  update(_delta: number): void {
    // GPU particle layers are self-animating; no per-frame CPU work needed.
    // Reserved for future per-frame effects (e.g. lightning bolt draw).
  }

  /** Fully tear down weather effects. Call from scene shutdown. */
  destroy(): void {
    this.stopRain();
    this.stopLightning();
    this.hideHorizonWarning();
    if (this.lightningOverlay) {
      this.lightningOverlay.destroy();
      this.lightningOverlay = null;
    }
    if (this.horizonOverlay) {
      this.horizonOverlay.destroy();
      this.horizonOverlay = null;
    }
  }

  // ── Rain ─────────────────────────────────────────────────────────────────

  private startRain(): void {
    if (this.rainLayer) return;
    if (!isWebGL(this.scene)) {
      // Canvas fallback — skip GPU particles
      return;
    }

    this.ensureRainTexture();
    const cam = this.scene.cameras.main;
    const vw  = cam.width;
    const vh  = cam.height;

    const texture = this.scene.textures.get(RAIN_TEXTURE_KEY);
    this.rainLayer = this.scene.add.spriteGPULayer(texture, RAIN_DROP_COUNT);
    this.rainLayer.setDepth(WEATHER_DEPTH);

    const intensity = WEATHER_WIND_INTENSITY[this.current];
    const fallSpeed = RAIN_FALL_SPEED * intensity;
    const driftX    = Math.sin(RAIN_ANGLE_RAD) * fallSpeed;
    const fallY     = Math.cos(RAIN_ANGLE_RAD) * fallSpeed;
    const fallDur   = (vh / fallY) * 1000;
    const driftDur  = driftX > 0 ? (vw / driftX) * 1000 : 0;

    for (let i = 0; i < RAIN_DROP_COUNT; i++) {
      const startX = Math.random() * (vw + 60) - 30;
      const startY = Math.random() * vh;
      const scale  = 0.5 + Math.random() * 0.7;
      const alpha  = 0.25 + Math.random() * 0.35;

      this.rainLayer.addMember({
        x: driftDur > 0
          ? { base: startX, amplitude: vw * 0.15, ease: 'Linear', duration: driftDur, loop: true }
          : startX,
        y: { base: startY - vh, amplitude: vh, ease: 'Linear', duration: fallDur * (0.8 + Math.random() * 0.4), loop: true, delay: Math.random() * fallDur },
        scaleX: scale * 0.15,
        scaleY: scale,
        rotation: RAIN_ANGLE_RAD,
        originX: 0.5,
        originY: 0,
        alpha,
        tintTopLeft:     0x9ec8e8,
        tintTopRight:    0x9ec8e8,
        tintBottomLeft:  0xaad4f0,
        tintBottomRight: 0xaad4f0,
        tintBlend: 1,
        tintMode:  0,
        scrollFactorX: 0,
        scrollFactorY: 0,
        frame: 0,
      });
    }
  }

  private stopRain(): void {
    if (this.rainLayer) {
      this.rainLayer.destroy();
      this.rainLayer = null;
    }
  }

  private ensureRainTexture(): void {
    if (this.scene.textures.exists(RAIN_TEXTURE_KEY)) return;
    const gfx = this.scene.make.graphics({}, false);
    gfx.fillStyle(0xffffff, 1);
    gfx.fillRect(0, 0, 2, 14);
    const rt = this.scene.add.renderTexture(0, 0, 2, 14);
    rt.draw(gfx, 0, 0);
    rt.render();
    rt.saveTexture(RAIN_TEXTURE_KEY);
    rt.destroy();
    gfx.destroy();
  }

  // ── Lightning ────────────────────────────────────────────────────────────

  private startLightning(): void {
    if (this.lightningTimer) return;
    this.ensureLightningOverlay();
    this.scheduleLightning();
  }

  private stopLightning(): void {
    if (this.lightningTimer) {
      this.lightningTimer.destroy();
      this.lightningTimer = null;
    }
    if (this.lightningOverlay) {
      this.scene.tweens.killTweensOf(this.lightningOverlay);
      this.lightningOverlay.setAlpha(0);
    }
  }

  private ensureLightningOverlay(): void {
    if (this.lightningOverlay) return;
    const cam = this.scene.cameras.main;
    this.lightningOverlay = this.scene.add.rectangle(
      cam.width / 2, cam.height / 2,
      cam.width, cam.height,
      0xfff8e8, 0,
    );
    this.lightningOverlay.setScrollFactor(0);
    this.lightningOverlay.setDepth(LIGHTNING_DEPTH);
    (this.scene as any).addToUI?.(this.lightningOverlay);
  }

  private scheduleLightning(): void {
    const delay = LIGHTNING_MIN_MS + Math.random() * (LIGHTNING_MAX_MS - LIGHTNING_MIN_MS);
    this.lightningTimer = this.scene.time.delayedCall(delay, () => {
      if (this.current !== 'storm') return;
      this.flashLightning();
      this.scheduleLightning(); // re-schedule for next flash
    });
  }

  private flashLightning(): void {
    if (!this.lightningOverlay) return;

    // Notify audio system hook for thunder sound
    this.scene.events.emit('weather:thunderClap', {
      weather: this.current,
    });

    // Double-flash visual effect
    this.scene.tweens.add({
      targets:  this.lightningOverlay,
      alpha:    { from: 0.85, to: 0 },
      duration: LIGHTNING_FLASH_MS,
      ease:     'Expo.easeOut',
      onStart:  () => { this.lightningOverlay?.setAlpha(0.85); },
      yoyo:     false,
    });
  }

  // ── Horizon warning ──────────────────────────────────────────────────────

  private showHorizonWarning(incoming: WeatherState): void {
    const tint = incoming === 'storm' ? 0x1a1a3a : 0x334455;
    if (!this.horizonOverlay) {
      const cam = this.scene.cameras.main;
      this.horizonOverlay = this.scene.add.rectangle(
        cam.width / 2, 0,
        cam.width, cam.height * 0.35,
        tint, 0,
      );
      this.horizonOverlay.setOrigin(0.5, 0);
      this.horizonOverlay.setScrollFactor(0);
      this.horizonOverlay.setDepth(HORIZON_DEPTH);
      (this.scene as any).addToUI?.(this.horizonOverlay);
    }

    this.scene.tweens.killTweensOf(this.horizonOverlay);
    this.horizonOverlay.setFillStyle(tint, 0);
    this.scene.tweens.add({
      targets:  this.horizonOverlay,
      alpha:    0.45,
      duration: HORIZON_FADE_MS,
      ease:     'Sine.easeIn',
    });
  }

  private hideHorizonWarning(): void {
    if (!this.horizonOverlay) return;
    this.scene.tweens.killTweensOf(this.horizonOverlay);
    this.scene.tweens.add({
      targets:  this.horizonOverlay,
      alpha:    0,
      duration: HORIZON_FADE_MS,
      ease:     'Sine.easeOut',
    });
  }

  // ── Gameplay hooks ───────────────────────────────────────────────────────

  private emitGameplayHooks(prev: WeatherState, next: WeatherState): void {
    const wet = next === 'rain' || next === 'storm';

    // Wet ground (§14.10 — conducts electricity)
    if ((prev !== 'rain' && prev !== 'storm') !== !wet) {
      this.scene.events.emit(WeatherSystem.EVENTS.WET_GROUND, { wet });
    }

    // Wind arrow deflection (§14.10)
    this.scene.events.emit(WeatherSystem.EVENTS.WIND_ARROWS, {
      intensity:  WEATHER_WIND_INTENSITY[next],
      direction:  WEATHER_WIND_DIRECTION[next],
    });

    // NPC shelter — run inside during rain/storm (§14.7)
    this.scene.events.emit(WeatherSystem.EVENTS.NPC_SHELTER, {
      seek: wet,
    });

    // Phantom shop — only active during storm (§5.9)
    this.scene.events.emit(WeatherSystem.EVENTS.PHANTOM_SHOP, {
      active: next === 'storm',
    });

    // Plant growth — active during rain (§14.3)
    this.scene.events.emit(WeatherSystem.EVENTS.PLANT_GROWTH, {
      active: next === 'rain',
    });

    // Windmill power — powered during storm (§14)
    this.scene.events.emit(WeatherSystem.EVENTS.WINDMILL_POWER, {
      powered: next === 'storm',
    });
  }
}
